import { isNaN } from 'lodash';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from 'react-query';
import { Automation, AutomationsApi } from 'api/automations';

export const AutomationByReferenceId = (
  refType: string,
  id: number,
): (string | number)[] => ['automation', refType, id];

export const AutomationById = (automationId: number): (string | number)[] => [
  'automation',
  automationId,
];

interface Reference {
  refType: string;
  refId: number;
  templateId: number;
}

export const useGetAutomationsForReferenceId = ({
  refId,
  refType,
  templateId,
}: Reference): UseQueryResult<Array<Automation>> =>
  useQuery(AutomationByReferenceId(refType, refId), () =>
    AutomationsApi.getAutomationForReferenceId(templateId, refType, refId),
  );

export const useGetAutomation = ({
  automationId,
}: Pick<Automation, 'automationId'>): UseQueryResult<Automation> =>
  useQuery(AutomationById(automationId), () =>
    isNaN(automationId)
      ? undefined
      : AutomationsApi.getAutomation(automationId),
  );

type CreateAutomationPayload = Reference & {
  automation: Automation;
};

export const useCreateAutomationForReferenceId = (): UseMutationResult<
  Automation,
  unknown,
  CreateAutomationPayload
> =>
  useMutation(
    ({ refId, refType, automation, templateId }: CreateAutomationPayload) =>
      AutomationsApi.createAutomationForReferenceId(
        templateId,
        refType,
        refId,
        automation,
      ),
  );

type UpdateAutomationPayload = {
  automationId: number;
  automation: Partial<Automation>;
};

export const useEditAutomation = (): UseMutationResult<
  Automation,
  unknown,
  UpdateAutomationPayload
> =>
  useMutation(({ automationId, automation }) =>
    AutomationsApi.updateAutomation(automationId, automation),
  );

export const useDeleteAutomation = (): UseMutationResult<
  unknown,
  unknown,
  Pick<Automation, 'automationId'>
> =>
  useMutation(({ automationId }) =>
    AutomationsApi.deleteAutomation(automationId),
  );

export const useAddAutomation = () =>
  useMutation(
    ({
      automation,
      referenceId,
      referenceType,
      templateId,
    }: {
      templateId: number;
      referenceType: string;
      referenceId: number;
      automation: Partial<Automation>;
    }) =>
      AutomationsApi.createAutomationForReferenceId(
        templateId,
        referenceType,
        referenceId,
        automation,
      ),
  );
